import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { studentApi, setStudentToken } from "../lib/studentAuth";

const CourseLogin = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendOpen, setResendOpen] = useState(false);
    const [resendEmail, setResendEmail] = useState("");
    const [resending, setResending] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await api.post("/students/login", { email: email.trim().toLowerCase(), password });
            setStudentToken(r.data.token);
            // Pick destination based on which course(s) the student owns
            const me = await studentApi.get("/students/me");
            const ids = me.data?.course_ids || [];
            toast.success("Welcome back.");
            if (ids.includes("battlefield-course")) {
                navigate("/course/dashboard");
            } else if (ids.includes("daily-battle-drill")) {
                navigate("/daily-battle-drill");
            } else {
                navigate("/courses");
            }
        } catch (err) {
            const msg = err.response?.data?.detail || "Login failed.";
            toast.error(typeof msg === "string" ? msg : "Login failed.");
        } finally {
            setLoading(false);
        }
    };

    const resendLink = async (e) => {
        e.preventDefault();
        const target = (resendEmail || email).trim().toLowerCase();
        if (!target) {
            toast.error("Enter the email you used to purchase.");
            return;
        }
        setResending(true);
        try {
            await api.post("/students/resend-access", { email: target });
            toast.success("If we found a purchase under that email, an access link is on the way.");
            setResendOpen(false);
            setResendEmail("");
        } catch {
            toast.error("Couldn't send right now. Try again in a moment.");
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="bg-black text-white min-h-screen flex items-center justify-center px-6 py-20" data-testid="course-login-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <div className="font-stencil tracking-widest text-xs text-red-600 mb-6">[ COURSE LOGIN ]</div>
                    <h1 className="font-display text-4xl uppercase tracking-tighter leading-[0.9]">
                        Welcome <span className="text-red-600">back.</span>
                    </h1>
                </div>
                <form onSubmit={submit} className="space-y-3 border border-white/10 bg-[#0a0a0a] p-8" data-testid="course-login-form">
                    <input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                        data-testid="course-login-email"
                        autoComplete="email"
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                        data-testid="course-login-password"
                        autoComplete="current-password"
                    />
                    <button type="submit" disabled={loading} className="w-full btn-primary justify-center disabled:opacity-50" data-testid="course-login-submit">
                        <LogIn size={16} /> {loading ? "Signing in..." : "Sign in"}
                    </button>
                </form>

                <div className="mt-6 text-center">
                    {!resendOpen ? (
                        <button
                            type="button"
                            onClick={() => { setResendOpen(true); setResendEmail(email); }}
                            className="text-sm text-white/55 hover:text-red-600 transition-colors inline-flex items-center gap-2"
                            data-testid="course-login-resend-toggle"
                        >
                            <Mail size={14} /> Lost your access link?
                        </button>
                    ) : (
                        <form onSubmit={resendLink} className="space-y-3 text-left border border-white/10 bg-[#0a0a0a] p-6" data-testid="course-login-resend-form">
                            <div className="font-stencil tracking-widest text-[10px] text-red-600">[ RESEND ACCESS LINK ]</div>
                            <p className="text-xs text-white/55">
                                Enter the email you used at checkout. If we find a purchase, we&apos;ll email a fresh access link.
                            </p>
                            <input
                                type="email"
                                placeholder="your@email.com"
                                value={resendEmail}
                                onChange={(e) => setResendEmail(e.target.value)}
                                required
                                className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="course-login-resend-email"
                                autoComplete="email"
                            />
                            <div className="flex gap-2">
                                <button type="submit" disabled={resending} className="btn-primary justify-center flex-1 disabled:opacity-50" data-testid="course-login-resend-submit">
                                    <Mail size={14} /> {resending ? "Sending..." : "Email me the link"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setResendOpen(false)}
                                    className="text-xs text-white/45 hover:text-white/80 px-3"
                                    data-testid="course-login-resend-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                <div className="mt-6 text-center">
                    <Link to="/courses" className="text-sm text-white/50 hover:text-red-600 transition-colors inline-flex items-center gap-2" data-testid="course-login-enroll-link">
                        Not enrolled yet? See the courses <ArrowUpRight size={14} />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CourseLogin;
